import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppContext } from '../../context/AppContext'
import FlowNode from './FlowNode'

import './FlowVisualizer.css'

function FlowVisualizer() {
  const { state, dispatch, actions } = useAppContext()
  const navigate = useNavigate()

  // Handle memory node click
  const handleMemoryClick = () => {
    dispatch({ type: actions.SET_ACTIVE_MODAL, payload: 'memory' })
  }

  // Handle knowledge node click - navigate to Organization Information Sources
  const handleKnowledgeClick = () => {
    navigate('/organization?tab=information-sources')
  }

  // Handle flow visibility toggle
  const handleToggleFlow = () => {
    console.log('FlowVisualizer toggle clicked, current state:', state.flowVisible)
    dispatch({ type: actions.SET_FLOW_VISIBLE, payload: !state.flowVisible })
    console.log('SET_FLOW_VISIBLE dispatched with payload:', !state.flowVisible)
  }

  // Demo flow animation effect
  useEffect(() => {
    if (state.flowStatus === 'processing') {
      // Simulate flow progression
      const steps = [
        { node: 'user-node', delay: 0 },
        { node: 'supervisor-node', delay: 500 },
        { node: 'memory-node', delay: 1000 },
        { node: 'knowledge-node', delay: 1500 },
        { node: 'agent1-node', delay: 2000 },
        { node: 'agent2-node', delay: 2200 },
        { node: 'agent3-node', delay: 2400 }
      ]

      steps.forEach(({ node, delay }) => {
        setTimeout(() => {
          dispatch({ 
            type: actions.SET_ACTIVE_NODES, 
            payload: [...state.activeNodes, node] 
          })
        }, delay)
      })

      // Clear active nodes after completion
      setTimeout(() => {
        dispatch({ type: actions.SET_ACTIVE_NODES, payload: [] })
      }, 4000)
    }
  }, [state.flowStatus, dispatch, actions, state.activeNodes])

  return (
    <section className="flow-visualizer">
      <div className="panel-card">
        <div className="flow-header">
          <h2><i className="fas fa-project-diagram"></i> Query Flow</h2>
          <button 
            className="flow-toggle-btn"
            onClick={handleToggleFlow}
            title={state.flowVisible ? "Hide flow" : "Show flow"}
          >
            <i className={`fas ${state.flowVisible ? 'fa-eye-slash' : 'fa-eye'}`}></i>
            {state.flowVisible ? 'Hide' : 'Show'}
          </button>
        </div>
        {state.flowVisible && (
          <div className="flow-diagram">
          
          {/* Row 1: User Node (Top Center) */}
          <FlowNode
            id="user-node"
            className="user-node"
            active={state.activeNodes.includes('user-node')}
          >
            USER
          </FlowNode>

          {/* Row 2: Database, Supervisor, Knowledge Base (Middle Row) */}
          <FlowNode
            id="memory-node"
            className="memory-node"
            active={state.activeNodes.includes('memory-node')}
            onClick={handleMemoryClick}
            clickable
          >
            DATABASE
          </FlowNode>

          <FlowNode
            id="supervisor-node"
            className="supervisor-node"
            active={state.activeNodes.includes('supervisor-node')}
          >
            <span>SUPERVISOR AGENT</span>
            <span>Clabs</span>
          </FlowNode>

          <FlowNode
            id="knowledge-node"
            className="knowledge-node"
            active={state.activeNodes.includes('knowledge-node')}
          >
            KNOWLEDGE BASE
            <button 
              className="view-contents-btn" 
              onClick={handleKnowledgeClick}
              title="View Knowledge Base Contents"
            >
              <i className="fas fa-eye"></i>
            </button>
          </FlowNode>

          {/* Row 3: Agent Nodes (Bottom Row) */}
          <FlowNode
            id="agent1-node"
            className="agent-node"
            active={state.activeNodes.includes('agent1-node')}
          >
            <span>AGENT 1</span>
            <span>ChatGPT</span>
          </FlowNode>

          <FlowNode
            id="agent2-node"
            className="agent-node"
            active={state.activeNodes.includes('agent2-node')}
          >
            <span>AGENT 2</span>
            <span>Claude</span>
          </FlowNode>

          <FlowNode
            id="agent3-node"
            className="agent-node"
            active={state.activeNodes.includes('agent3-node')}
          >
            <span>AGENT 3</span>
            <span>Gemini</span>
          </FlowNode>

          {/* Flow Arrows SVG */}
          <svg className="flow-arrows" width="100%" height="100%">
            <defs>
              <marker id="arrowhead" markerWidth="10" markerHeight="8" 
                      refX="9" refY="4" orient="auto">
                <polygon points="0 0, 10 4, 0 8" fill="#64b5f6" stroke="#fff" strokeWidth="0.5" />
              </marker>
              <marker id="arrowhead-active" markerWidth="12" markerHeight="9" 
                      refX="10" refY="4.5" orient="auto">
                <polygon points="0 0, 12 4.5, 0 9" fill="#64b5f6" stroke="#fff" strokeWidth="0.8" />
              </marker>
            </defs>
            
            {/* User to Supervisor */}
            <path id="arrow1" className="flow-arrow query" d="M 450 170 L 450 280" markerEnd="url(#arrowhead)" />
            
            {/* Supervisor to Database */}
            <path id="arrow2" className="flow-arrow context" d="M 360 325 L 180 325" markerEnd="url(#arrowhead)" />
            
            {/* Supervisor to Knowledge Base */}
            <path id="arrow3" className="flow-arrow knowledge" d="M 540 325 L 675 325" markerEnd="url(#arrowhead)" />
            
            {/* Supervisor to Agents */}
            <path id="arrow4" className="flow-arrow route" d="M 380 370 L 180 480" markerEnd="url(#arrowhead)" />
            <path id="arrow5" className="flow-arrow route" d="M 450 370 L 450 480" markerEnd="url(#arrowhead)" />
            <path id="arrow6" className="flow-arrow route" d="M 520 370 L 720 480" markerEnd="url(#arrowhead)" />
            
            {/* Database to Supervisor */}
            <path id="arrow7" className="flow-arrow response" d="M 180 325 L 360 325" markerEnd="url(#arrowhead)" />
            
            {/* Knowledge Base to Supervisor */}
            <path id="arrow8" className="flow-arrow response" d="M 675 325 L 540 325" markerEnd="url(#arrowhead)" />
            
            {/* Agents back to Supervisor */}
            <path id="arrow9" className="flow-arrow response" d="M 180 480 L 380 370" markerEnd="url(#arrowhead)" />
            <path id="arrow10" className="flow-arrow response" d="M 450 480 L 450 370" markerEnd="url(#arrowhead)" />
            <path id="arrow11" className="flow-arrow response" d="M 720 480 L 520 370" markerEnd="url(#arrowhead)" />
            
            {/* Supervisor back to User */}
            <path id="arrow12" className="flow-arrow query" d="M 450 280 L 450 170" markerEnd="url(#arrowhead)" />
          </svg>
          </div>
        )}
      </div>
    </section>
  )
}

export default FlowVisualizer
